import React from 'react';
import { useTranslation } from '../i18n';
import moment from 'moment';
import { achievements } from './achievements';

export const formatTime = (time) => {
  const hours = String(Math.floor(time / 3600)).padStart(2, '0');
  const minutes = String(Math.floor((time % 3600) / 60)).padStart(2, '0');
  const seconds = String(time % 60).padStart(2, '0');
  return `${hours}:${minutes}:${seconds}`;
};

export const prettySecondsToTime = (seconds) => {
  const lang = localStorage.getItem('language');
  let h = 'h';
  let min = 'min';
  let sec = 'sec';

  switch (lang) {
    case 'sv':
      h = 'tim';
      min = 'min';
      sec = 'sek.';
      break;
  }

  // Calculate hours, minutes and seconds
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  // Format the output based on the available time
  let result = '';
  if (hours > 0) {
    result += hours + ` ${h}`;
  }
  if (minutes > 0) {
    if (result.length > 0) {
      result += ' ';
    }
    result += minutes + ` ${min}`;
  }
  if (result.length === 0) {
    result = remainingSeconds + ` ${sec}`;
  }

  return result;
};

export const deleteLocalStorage = () => {
  localStorage.removeItem('globalState');
  localStorage.removeItem('searchHistory');
};

const parseSessionsData = (sessions, weekstart) => {
  const weekActivity = {
    Mon: '',
    Tue: '',
    Wed: '',
    Thu: '',
    Fri: '',
    Sat: '',
    Sun: '',
  };

  // Helper function to calculate start of the week based on preference
  const calculateStartOfWeek = (date, weekstart) => {
    const day = date.getDay(); // Sunday = 0, Monday = 1, ..., Saturday = 6
    const diff = weekstart === 'mon' ? day - 1 : day; // Adjust for Monday or Sunday start
    const adjustedDiff = diff < 0 ? diff + 7 : diff; // Handle Sunday case
    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() - adjustedDiff);
    startOfWeek.setHours(0, 0, 0, 0);
    return startOfWeek;
  };

  // Get the current date
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Calculate the start and end of the week
  const startOfWeek = calculateStartOfWeek(today, weekstart);
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);
  endOfWeek.setHours(23, 59, 59, 999);

  // Populate weekActivity based on sessions
  sessions.forEach((session) => {
    const sessionDate = new Date(
      session.date.seconds * 1000 + session.date.nanoseconds / 1000000,
    );
    if (sessionDate >= startOfWeek && sessionDate <= endOfWeek) {
      const dayOfWeek = sessionDate.toLocaleDateString('en-US', {
        weekday: 'short',
      });
      weekActivity[dayOfWeek] = 'active';
    }
  });

  // Update weekActivity for future and past days
  const weekDays =
    weekstart === 'mon'
      ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
      : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  weekDays.forEach((day, index) => {
    const dayDate = new Date(startOfWeek);
    dayDate.setDate(startOfWeek.getDate() + index);
    if (dayDate > today) {
      weekActivity[day] = ''; // Future days
    } else if (weekActivity[day] !== 'active') {
      weekActivity[day] = 'inactive'; // Past days with no activity
    }
  });

  return weekActivity;
};

export const generateSessionStreak = (sessions, weekstart) => {
  const { t } = useTranslation();

  // Define weekdays starting from Monday
  let weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  // Adjust the order if the week starts on Sunday
  if (weekstart === 'sun') {
    weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  }

  const week = {
    Mon: t.mon,
    Tue: t.tue,
    Wed: t.wed,
    Thu: t.thu,
    Fri: t.fri,
    Sat: t.sat,
    Sun: t.sun,
  };

  const weekActivity = parseSessionsData(sessions, weekstart);

  return weekDays.map((day) => (
    <div key={day} className="day-wrapper">
      <div className={`day ${weekActivity[day]}`}></div>
      <div className="day-label">{week[day]}</div>
    </div>
  ));
};

export const generateBookKey = (pre) => {
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const length = 12;
  let key = pre;
  const charactersLength = characters.length;

  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * charactersLength);
    key += characters.charAt(randomIndex);
  }

  return key;
};

export const readingStatus = (status) => {
  const lang = localStorage.getItem('language');

  let currentlyReading = 'Currently reading';
  let wantToRead = 'Want to read';
  let read = 'Read';

  switch (lang) {
    case 'sv':
      currentlyReading = 'Läser nu';
      wantToRead = 'Vill läsa';
      read = 'Läst';
      break;
  }

  let string;
  switch (status) {
    case 'reading':
      string = currentlyReading;
      break;
    case 'wantToRead':
      string = wantToRead;
      break;
    case 'read':
      string = read;
      break;
    default:
      string = 'Unknown';
      break;
  }
  return string;
};

const detectMobile = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;

  if (/android/i.test(userAgent)) {
    return 'Android';
  }
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return 'iOS';
  }
  return 'Desktop';
};

export const setMaxWidth = () => {
  const deviceType = detectMobile();
  const root = document.documentElement;

  if (deviceType === 'Desktop') {
    root.classList.add('desktop');
  } else {
    root.classList.remove('desktop');
  }
};

export function calculateStreak(sessions, previousStreakStartDate = null) {
  const today = moment().startOf('day');
  if (!sessions?.length) {
    // Return streak 1 and todays date
    return {
      longestStreak: 1,
      currentStreak: 1,
      streakStartDate: today.toISOString(),
      streakEndDate: today.toISOString(),
      lastStreakDate: today.toISOString(),
    };
  }

  // Sort sessions by date in ascending order
  const sortedSessions = [...sessions].sort(
    (a, b) => a.date.seconds - b.date.seconds,
  );

  let longestStreak = 1;
  let currentStreak = 1;
  let streakStartDate = null;
  let streakEndDate = null;

  // Convert previousStreakStartDate to moment object if available
  const previousStreakDate = previousStreakStartDate
    ? moment(previousStreakStartDate)
    : null;

  // Initialize streak with the first session date, ignoring the time
  let previousDate = moment.unix(sortedSessions[0].date.seconds).startOf('day');
  let currentStreakStartDate = previousDate;

  for (let i = 0; i < sortedSessions.length; i++) {
    const currentDate = moment
      .unix(sortedSessions[i].date.seconds)
      .startOf('day');

    // Skip if the session is older than previousStreakStartDate
    if (previousStreakDate && currentDate.isBefore(previousStreakDate, 'day')) {
      continue;
    }

    const diffDays = currentDate.diff(previousDate, 'days');

    if (diffDays === 1) {
      // Continue the current streak
      currentStreak++;
    } else if (diffDays > 1) {
      // End of a streak, compare with the longest streak
      if (currentStreak > longestStreak) {
        longestStreak = currentStreak;
        streakStartDate = currentStreakStartDate;
        streakEndDate = previousDate;
      }
      // Start a new streak
      currentStreak = 1;
      currentStreakStartDate = currentDate;
    }

    previousDate = currentDate;
  }

  // Final check for the last streak
  if (currentStreak > longestStreak) {
    longestStreak = currentStreak;
    streakStartDate = currentStreakStartDate;
    streakEndDate = previousDate;
  }

  return {
    currentStreak,
    longestStreak,
    streakStartDate: streakStartDate ? streakStartDate.toISOString() : null,
    streakEndDate: streakEndDate ? streakEndDate.toISOString() : null,
    lastStreakDate: today.toISOString(),
  };
}

export const propsToText = (input, limit = null) => {
  if (Array.isArray(input)) {
    const outputArray = limit ? input.slice(0, limit) : input;
    return outputArray.join(', ');
  } else {
    return input;
  }
};

export const isEmpty = (obj) => {
  return Object.keys(obj).length === 0;
};

export const getAchievementLevel = (level) => {
  let className = 'bronze';
  switch (level) {
    case 1:
      className = 'bronze';
      break;
    case 2:
      className = 'silver';
      break;
    case 3:
      className = 'gold';
      break;
    case 4:
      className = 'platinum';
      break;
  }
  return className;
};

export const getUniqueAchievementLevels = (achievementsUnlocked) => {
  if (typeof achievementsUnlocked === 'undefined') {
    return [];
  }
  // Map the achievement IDs to their corresponding levels
  const levels = achievementsUnlocked.map((id) => {
    const achievement = achievements.find((ach) => ach.id === id);
    return achievement ? achievement.level : null;
  });

  // Filter out null values
  const filteredLevels = levels.filter((level) => level !== null);

  // Create an array to store unique levels
  const uniqueLevels = [];

  // Iterate through the filtered levels
  filteredLevels.forEach((level) => {
    // Add the level to uniqueLevels array only if it's not already included
    if (!uniqueLevels.includes(level)) {
      uniqueLevels.push(level);
    }
  });

  return uniqueLevels;
};

import bookmarkPlatinum from '../images/bookmark-platinum.svg';
import bookmarkGold from '../images/bookmark-gold.svg';
import bookmarkSilver from '../images/bookmark-silver.svg';
import bookmarkBronze from '../images/bookmark-bronze.svg';
import bookmarkDefault from '../images/bookmark-default.svg';

export const bookmarkLink = (tier) => {
  let bookmark;
  switch (tier) {
    case 4:
      bookmark = bookmarkPlatinum;
      break;
    case 3:
      bookmark = bookmarkGold;
      break;
    case 2:
      bookmark = bookmarkSilver;
      break;
    case 1:
      bookmark = bookmarkBronze;
      break;
    default:
      bookmark = bookmarkDefault;
      break;
  }
  return bookmark;
};

export function analyzeReadingPatterns(entries) {
  const startTime = {
    morning: 6, // 6 AM - 12 PM
    afternoon: 12, // 12 PM - 6 PM
    evening: 18, // 6 PM - 12 PM
    night: 24, // 12 PM - 6 AM
  };

  const timeCategories = {
    morning: 0,
    afternoon: 0,
    evening: 0,
    night: 0,
  };

  const dayCategories = {
    weekdays: 0,
    weekends: 0,
  };

  let totalSessions = 0;

  entries.forEach((entry) => {
    totalSessions += 1;
    // Convert seconds to a JavaScript Date object
    const date = new Date(entry.date.seconds * 1000);

    // Extract hour and day of the week
    const hour = date.getHours();
    const dayOfWeek = date.getDay();

    // Determine time of day
    if (hour >= startTime.morning && hour < startTime.afternoon) {
      timeCategories.morning += 1;
    } else if (hour >= startTime.afternoon && hour < startTime.evening) {
      timeCategories.afternoon += 1;
    } else if (hour >= startTime.evening && hour < startTime.night) {
      timeCategories.evening += 1;
    } else {
      timeCategories.night += 1;
    }

    // Determine if it's a weekday or weekend
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      // Sunday or Saturday
      dayCategories.weekends += 1;
    } else {
      dayCategories.weekdays += 1;
    }
  });

  // Calculate majority categories
  const majorityTimeOfDay = Object.keys(timeCategories).reduce((a, b) =>
    timeCategories[a] > timeCategories[b] ? a : b,
  );

  const majorityDayType =
    dayCategories.weekends > dayCategories.weekdays ? 'weekends' : 'weekdays';

  return {
    timeCategories,
    dayCategories,
    majorityTimeOfDay,
    majorityDayType,
    totalSessions,
  };
}

export function getSuperlative(streak, t) {
  const superlatives = [
    [
      t.superlatives.l1.first,
      t.superlatives.l1.second,
      t.superlatives.l1.third,
      t.superlatives.l1.fourth,
    ], // 0-27 day streak
    [
      t.superlatives.l2.first,
      t.superlatives.l2.second,
      t.superlatives.l2.third,
      t.superlatives.l2.fourth,
    ], // 28-55 day streak
    [
      t.superlatives.l3.first,
      t.superlatives.l3.second,
      t.superlatives.l3.third,
      t.superlatives.l3.fourth,
    ], // 56-83 day streak
    [
      t.superlatives.l4.first,
      t.superlatives.l4.second,
      t.superlatives.l4.third,
      t.superlatives.l4.fourth,
    ], // 84-111 day streak
    [
      t.superlatives.l5.first,
      t.superlatives.l5.second,
      t.superlatives.l5.third,
      t.superlatives.l5.fourth,
    ], // 112+ day streak
  ];

  // Determine the level based on the streak
  const levelIndex = Math.min(Math.floor(streak / 28), superlatives.length - 1);

  // Combine superlatives from all previous levels up to the current level
  const allSuperlatives = superlatives.slice(0, levelIndex + 1).flat();

  // Randomly pick one from the combined list
  const randomIndex = Math.floor(Math.random() * allSuperlatives.length);
  return allSuperlatives[randomIndex];
}

export function updateProtocol(url) {
  if (!url) {
    return '';
  }
  try {
    const urlObj = new URL(url);

    if (urlObj.hostname.includes('books.google.com')) {
      urlObj.protocol = 'https:';
    }

    // if (urlObj.searchParams.has('zoom')) {
    //   urlObj.searchParams.set('zoom', '0');
    // }

    return urlObj.toString();
  } catch (error) {
    console.error('Not a books.google.com url:', error);
    return url;
  }
}
